/**
 * AdminPulseStrip — 4-tile at-a-glance row at the top of /admin
 * (Directive §2 rebuild).
 *
 * • Uses MetricCardHeader with real sparklines from a client-side
 *   ring buffer that captures each 20 s poll.
 * • DeltaBadge derived from the delta between the previous two samples,
 *   in the accessible data palette (blue / orange / gray).
 * • Poll interval: 20 s (cheap endpoint; count-only queries).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Activity, Inbox, Users2, Wallet } from "lucide-react";
import { api, fmtKES } from "@/lib/api";
import MetricCardHeader from "@/components/dashboard/MetricCardHeader";

const REFRESH_MS = 20_000;
const HISTORY_LIMIT = 30; // 30 samples · 20 s = 10 min window

/* Extract a numeric-only KES value for delta math (strips currency symbols). */
function numericKes(x) {
    if (typeof x === "number") return x;
    if (typeof x !== "string") return 0;
    const n = parseFloat(x.replace(/[^\d.-]+/g, ""));
    return Number.isNaN(n) ? 0 : n;
}

function computeDelta(current, previous) {
    if (previous === undefined || previous === null || previous === 0) return null;
    if (current === undefined || current === null) return null;
    const c = numericKes(current);
    const p = numericKes(previous);
    if (p === 0) return null;
    return ((c - p) / Math.abs(p)) * 100;
}

export default function AdminPulseStrip() {
    const [pulse, setPulse] = useState(null);
    const [error, setError] = useState(null);
    const [ticking, setTick] = useState(false);
    // Ring buffer of numeric samples for each of the 4 sparklines.
    const historyRef = useRef({
        users_online: [],
        trades_today: [],
        locked_in_kes: [],
        pending_upgrades: [],
    });
    const [historyTick, setHistoryTick] = useState(0);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setTick(true);
            try {
                const { data } = await api.get("/admin/pulse");
                if (cancelled) return;
                setPulse(data);
                setError(null);
                // Append to ring buffers.
                const buf = historyRef.current;
                const push = (k, v) => {
                    buf[k] = [...buf[k], numericKes(v)].slice(-HISTORY_LIMIT);
                };
                push("users_online", data.users_online);
                push("trades_today", data.trades_today);
                push("locked_in_kes", data.locked_in_kes);
                push("pending_upgrades", data.pending_upgrades);
                setHistoryTick((t) => t + 1);
            } catch (e) {
                if (!cancelled) setError(e?.response?.data?.detail || e.message);
            } finally {
                if (!cancelled) setTick(false);
            }
        };
        load();
        const id = setInterval(load, REFRESH_MS);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    const tiles = useMemo(() => {
        const buf = historyRef.current;
        const dOf = (arr) => arr.length >= 2 ? computeDelta(arr[arr.length - 1], arr[arr.length - 2]) : null;
        return [
            {
                icon: Users2,
                title: "Users online",
                value: pulse?.users_online ?? "–",
                subtitle: `Last ${pulse?.online_window_min ?? 5} min`,
                spark: buf.users_online,
                delta: dOf(buf.users_online),
                testid: "pulse-users-online",
            },
            {
                icon: Activity,
                title: "Trades today",
                value: pulse?.trades_today ?? "–",
                subtitle: "UTC day, all users",
                spark: buf.trades_today,
                delta: dOf(buf.trades_today),
                testid: "pulse-trades-today",
            },
            {
                icon: Wallet,
                title: "Revenue locked-in",
                value: pulse ? fmtKES(pulse.locked_in_kes) : "–",
                subtitle: "Annual plans · realised",
                spark: buf.locked_in_kes,
                delta: dOf(buf.locked_in_kes),
                testid: "pulse-revenue-locked-in",
            },
            {
                icon: Inbox,
                title: "Pending upgrades",
                value: pulse?.pending_upgrades ?? "–",
                subtitle: "Institutional requests",
                spark: buf.pending_upgrades,
                delta: dOf(buf.pending_upgrades),
                testid: "pulse-pending-upgrades",
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pulse, historyTick]);

    return (
        <div className="mb-4" data-testid="admin-pulse-strip">
            <div className="flex items-center gap-2 mb-2">
                <div className="relative flex items-center justify-center w-2 h-2">
                    <span className={`absolute inline-flex h-full w-full rounded-full bg-brand-green ${ticking ? "opacity-75 animate-ping" : "opacity-0"}`} />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-green" />
                </div>
                <span className="text-caption uppercase tracking-[0.18em] font-bold text-muted-foreground">
                    At-a-glance · live pulse
                </span>
                {error && <span className="text-caption text-brand-red ml-2">· {error}</span>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 density-compact:gap-3">
                {tiles.map((t) => (
                    <div
                        key={t.title}
                        className="flex flex-col p-6 density-compact:p-3.5 bg-card rounded-lg border border-border hover:bg-muted/40 transition-colors min-h-[140px]"
                    >
                        <MetricCardHeader {...t} />
                    </div>
                ))}
            </div>
        </div>
    );
}
